import {
  APIV1Version,
  ClientType,
  getClientType,
  getStoreName,
} from '@buncha/common/types/common';
import {
  ActivityIndicator,
  NativeScrollEvent,
  NativeSyntheticEvent,
  RefreshControl,
  RefreshControlProps,
} from 'react-native';
import {
  B2BScreenParam,
  OrderVerifyStatus,
  OrdersForValidation,
} from '@buncha/B2B/types';
import {Box, HBox} from '@buncha/components/core/view';
import {H5Text, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useState} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';
import {
  useGetOrdersForValidationAndUpdationCheckout,
  useOrderValidationUpdate,
} from '@buncha/B2B/hooks/B2BChekout';

import AddBottomSheet from '@buncha/batchDetails/addBottomSheet';
import {Analytics} from '@buncha/config/analytics/analytics';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import CancelOrderModal from '../CancelOrderModal';
import {CustomFlatList} from '@buncha/components/core/list';
import EmptyOrders from '../common/EmptyOrders';
import {EventType} from '@buncha/config/analytics/type';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import {ShopperScreens} from '@buncha/home/navigation/types';
import VerificationItems from '../VerificationItems/VerificationItems';
import {navigation} from '@buncha/config/navigation';
import {smartLook} from '@buncha/config/analytics';
import {styles} from './styles';
import {useB2BCheckoutContext} from '@buncha/B2B/hooks/B2BCheckoutReducer';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';

interface VerificationProps {
  loading: boolean;
  storeId?: number;
  storeIndex: string;
  onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
}

const Verification: React.FC<VerificationProps> = props => {
  const {loading, storeId, storeIndex} = props;
  const muliFont = useFontTheme('muli');
  const theme = useColorThemeType();
  const {state, dispatch} = useB2BCheckoutContext();
  const {body, button} = useGetLocale(Modules.B2B, HomeScreens.B2B) ?? {};
  const verificationItems = state.ordersForValidation;
  const [itemLoading, setItemLoading] = useState(false);
  const [getOrdersForValidationAndUpdation, checkoutState] =
    useGetOrdersForValidationAndUpdationCheckout(APIV1Version.V100, dispatch);
  const [orderValidationUpdate, updateState] = useOrderValidationUpdate(
    APIV1Version.V100,
    dispatch,
  );
  const [addOrderId, setAddOrderId] = useState<number>();
  const [addItemSheet, setAddItemSheet] = useState(false);
  const [modal, setModal] = useState(false);
  const routeParams = useRoute()?.params as B2BScreenParam | undefined;
  const runId = routeParams?.runId ?? 0;
  const isLoading = updateState?.loading || itemLoading;

  const orderVerifyStatus = verificationItems?.map(item => {
    return item.status;
  }, []);

  const orderIds = verificationItems
    ?.map(item => {
      const emptyOrder = !item?.order?.items?.length;
      if (!emptyOrder) return item?.order?.id;
    })
    .filter(id => id !== undefined);

  const disabledVerificationAll = Boolean(
    orderVerifyStatus?.filter(item => item !== OrderVerifyStatus.pending)
      ?.length,
  );
  const getLoading = useCallback((itemLoad: boolean) => {
    setItemLoading(itemLoad);
  }, []);

  const refresh = useCallback(() => {
    getOrdersForValidationAndUpdation(runId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSheet = useCallback(() => {
    setAddItemSheet(!addItemSheet);
  }, [addItemSheet]);

  const onClick = useCallback(() => {
    if (orderIds?.length) {
      const eventParams = {
        type: EventType.cta,
        runId: runId,
        countOfOrders: orderIds?.length,
        client: ClientType.meijer,
        storeType: getStoreName(String(storeId)),
      };
      Analytics.eventWithProps('send all for verification', eventParams);
      smartLook.trackCustomEvent('send all for verification', eventParams);
      orderValidationUpdate(orderIds, runId);
    }
  }, [orderIds, orderValidationUpdate, runId, storeId]);

  const toggleModal = useCallback(() => {
    setModal(!modal);
  }, [modal]);

  const navigateToAddItem = useCallback(() => {
    toggleSheet();
    if (!runId || !storeId || !addOrderId) return;
    Analytics.eventWithProps('Navigate to Add Item', {
      type: EventType.cta,
      runID: runId,
      client: getClientType(true),
      storeType: getStoreName(String(storeId)),
      orderId: addOrderId,
      storeId,
    });
    navigation.navigate(ShopperScreens.AddItemPage, {
      storeId,
      runId,
      orderId: addOrderId,
      storeIndex,
      returnPage: B2BScreens.B2BCheckout,
    });
  }, [toggleSheet, runId, storeId, addOrderId, storeIndex]);

  const openSheet = useCallback(
    (item: OrdersForValidation) => {
      setAddOrderId(item?.order?.id);
      toggleSheet();
    },
    [toggleSheet],
  );

  const renderItem = useCallback(
    ({item}: {item: OrdersForValidation}) => {
      return (
        <VerificationItems
          item={item}
          runId={runId}
          getLoading={getLoading}
          loading={updateState?.loading}
          storeId={storeId}
          addItem={openSheet}
          toggleCancelModal={toggleModal}
        />
      );
    },
    [getLoading, openSheet, runId, storeId, toggleModal, updateState?.loading],
  );

  return (
    <Box style={styles.container}>
      <HBox
        style={[
          styles.infoBox,
          {
            backgroundColor: theme?.background.yellow[100],
          },
        ]}>
        <H5Text
          style={[
            styles.disclaimerText,
            styles.iconText,
            {
              color: theme?.text?.blue?.[300],
              fontFamily: muliFont[900].normal,
            },
          ]}>
          {body?.verification?.[0]}
        </H5Text>
        <H6Text
          style={[
            styles.disclaimerText,
            styles.text,
            {
              color: theme?.text?.blue?.[300],
            },
          ]}>
          {body?.verification?.[1]}
        </H6Text>
      </HBox>
      <If condition={loading}>
        <ActivityIndicator size={'large'} style={styles.flexStyle} />
      </If>
      <If condition={!loading}>
        <CustomFlatList
          showsVerticalScrollIndicator={false}
          keyExtractor={({item}: {item: OrdersForValidation}) =>
            `${item?.order?.id}`
          }
          onScroll={props.onScroll}
          refreshControl={
            (
              <RefreshControl
                refreshing={checkoutState.loading}
                onRefresh={refresh}
                tintColor={theme?.background.gray[100]}
                style={styles.refreshControl}
              />
            ) as React.ReactElement<RefreshControlProps>
          }
          ListEmptyComponent={
            <Box style={styles.flexStyle}>
              <EmptyOrders bottomText={body?.noOrdersForVerification} />
            </Box>
          }
          contentContainerStyle={[
            styles.listSTyle,
            verificationItems?.length ? undefined : styles.flexStyle,
          ]}
          renderItem={renderItem}
          extraData={verificationItems}
          data={verificationItems}
        />
        <If
          condition={
            !disabledVerificationAll &&
            verificationItems?.length &&
            orderIds?.length
          }>
          <Box
            style={[
              styles.bottomDivider,
              {
                borderTopColor: theme?.background.gray[200],
              },
            ]}
          />
          <If condition={!itemLoading}>
            <PButton
              title={button?.verification}
              style={styles.buttonStyle}
              bgColor={theme?.background.blue[100]}
              disabledBgColor={'transparent'}
              titleDisableColor={theme?.text.blue[100]}
              borderDisableColor={theme?.text.blue[100]}
              disabled={isLoading}
              borderColor={theme?.background.blue[100]}
              borderWidth={1}
              isLoading={isLoading}
              changeStyleOnLoading
              loadingText={button?.processing}
              onPress={onClick}
              loadingIndicator={<ActivityIndicator />}
            />
          </If>
        </If>
        <If condition={addItemSheet}>
          <AddBottomSheet
            storeIndex={storeIndex}
            open={Boolean(addItemSheet)}
            onClose={toggleSheet}
            navigateToAddScreen={navigateToAddItem}
            item={undefined}
            orderId={addOrderId}
            runId={runId}
            storeId={storeId}
          />
        </If>
      </If>
      <If condition={modal}>
        <CancelOrderModal
          open={modal}
          onClose={toggleModal}
          hideTopCta
          onSubmit={toggleModal}
          loading={false}
          title={'Important Information!'}
          subTitle={
            'Please ask the Dispatcher to remove this order from our task list.'
          }
        />
      </If>
    </Box>
  );
};

export default React.memo(Verification);
